import { useState, useEffect } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { X, Sparkles, Lightbulb, Users, Globe, ExternalLink, Star, LogIn } from "lucide-react";
import { motion } from "framer-motion";

const scoreColor = (s) => (s == null ? "#64748B" : s >= 70 ? "#10B981" : s >= 50 ? "#EAB308" : "#F43F5E");
const scoreBg = (s) => (s == null ? "#64748B" : s >= 70 ? "#10B981" : s >= 50 ? "#EAB308" : "#F43F5E");

const statusPill = (status) => {
  const s = (status || "").toUpperCase();
  if (["BULLISH", "POSITIVE", "HEALTHY", "STRONGLY UNDERVALUED"].some((k) => s.includes(k))) return "bg-[var(--bull)]/15 text-bull border-[var(--bull)]/30";
  if (["BEARISH", "NEGATIVE", "RISKY", "OVERVALUED"].some((k) => s.includes(k))) return "bg-[var(--bear)]/15 text-bear border-[var(--bear)]/30";
  return "bg-[var(--gold)]/15 text-gold border-[var(--gold)]/30";
};

const ScoreBar = ({ score }) => (
  <div className="h-1.5 rounded-full bg-[var(--surface-base)] overflow-hidden">
    <div className="h-full rounded-full" style={{ width: `${score || 0}%`, backgroundColor: scoreColor(score) }} />
  </div>
);

const Metric = ({ label, tag, value, note, valueClass }) => (
  <div className="bg-[var(--surface-base)] rounded-lg p-3 border border-[var(--border-subtle)]">
    <div className="flex items-center justify-between mb-1">
      <span className="text-[11px] text-[var(--text-secondary)]">{label}</span>
      {tag && <span className="text-[9px] px-1.5 py-0.5 rounded bg-[var(--surface-hover)] text-[var(--text-muted)] font-mono-data">{tag}</span>}
    </div>
    <p className={`font-heading font-bold text-sm ${valueClass || ""}`}>{value}</p>
    {note && <p className="text-[10px] text-[var(--text-muted)] mt-0.5 leading-snug">{note}</p>}
  </div>
);

const MiniBar = ({ label, value }) => (
  <div>
    <div className="flex justify-between text-[11px] mb-1">
      <span className="text-[var(--text-secondary)]">{label}</span>
      <span className="font-mono-data font-bold" style={{ color: scoreColor(value) }}>{value}</span>
    </div>
    <div className="h-1 rounded-full bg-[var(--surface-base)] overflow-hidden"><div className="h-full" style={{ width: `${value || 0}%`, backgroundColor: scoreColor(value) }} /></div>
  </div>
);

const CardHead = ({ title, status, score }) => (
  <>
    <div className="flex items-center justify-between mb-3">
      <h3 className="font-heading text-lg font-bold">{title}</h3>
      {status && <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${statusPill(status)}`}>{status}</span>}
    </div>
    <div className="flex items-center justify-between text-xs mb-1"><span className="text-[var(--text-muted)]">Score</span><span className="font-mono-data font-bold" style={{ color: scoreColor(score) }}>{score}</span></div>
    <ScoreBar score={score} />
  </>
);

const QuickInsight = ({ pattern }) => (
  <div className="mt-4 mb-3 bg-[var(--surface-base)] rounded-lg p-3 border border-[var(--border-subtle)]">
    <div className="flex items-center gap-1.5 text-[11px] text-amber font-semibold mb-0.5"><Lightbulb size={12} /> QUICK INSIGHT</div>
    <p className="text-sm font-semibold">Pattern: <span className="text-accent">{pattern}</span></p>
  </div>
);

const sentClass = (s) => (s === "Positif" ? "text-bull" : s === "Negatif" ? "text-bear" : "text-amber");

export default function AnalysisModal({ ticker, onClose }) {
  const { user, login } = useAuth();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  const [needLogin, setNeedLogin] = useState(false);

  useEffect(() => {
    let alive = true;
    setD(null); setErr(false); setNeedLogin(false);
    if (!user) { setNeedLogin(true); return; }
    api.get(`/analysis/${ticker}`).then((r) => alive && setD(r.data)).catch((e) => {
      if (!alive) return;
      if (e?.response?.status === 401) setNeedLogin(true);
      else setErr(true);
    });
    return () => { alive = false; };
  }, [ticker, user]);

  const up = (d?.change_pct ?? 0) >= 0;

  return (
    <div className="fixed inset-0 z-[110] flex items-start justify-center overflow-y-auto p-3 sm:p-6 bg-black/75 backdrop-blur-sm" onClick={onClose} data-testid="analysis-modal">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-6xl my-4" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="card p-5 mb-4 relative overflow-hidden">
          <button onClick={onClose} data-testid="analysis-close" className="absolute top-4 right-4 p-2 rounded-md hover:bg-[var(--surface-hover)] text-[var(--text-secondary)]"><X size={18} /></button>
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 rounded-xl bg-[var(--accent)]/12 border border-[var(--accent)]/25 flex items-center justify-center shrink-0">
              <span className="font-heading text-xl font-extrabold text-accent">{ticker.slice(0, 2)}</span>
            </div>
            <div className="flex-1 min-w-0 pr-10">
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="font-heading text-3xl font-extrabold">{ticker}</h2>
                <span className="px-1.5 py-0.5 rounded bg-[var(--accent)]/15 text-accent text-[10px] font-bold flex items-center gap-1"><Sparkles size={10} /> AI</span>
              </div>
              <p className="text-sm text-[var(--text-secondary)]">{d?.name || "Memuat..."}</p>
              {d && <p className="text-xs text-[var(--text-muted)] mt-0.5">{d.sector}{d.sub_sector ? ` · ${d.sub_sector}` : ""}</p>}
              {d?.is_syariah && <span className="inline-block mt-1.5 text-[10px] px-2 py-0.5 rounded-full bg-[var(--bull)]/15 text-bull border border-[var(--bull)]/30">☪ Syariah</span>}
            </div>
            {d && (
              <div className="text-right shrink-0 mr-8">
                <p className={`font-heading text-2xl font-extrabold ${up ? "text-bull" : "text-bear"}`}>{d.price != null ? `Rp ${d.price.toLocaleString("id-ID")}` : "-"}</p>
                <p className={`text-sm font-mono-data ${up ? "text-bull" : "text-bear"}`}>{up ? "▲ +" : "▼ "}{d.change_pct}%</p>
                <p className="text-[10px] text-[var(--text-muted)] mt-1">Update: {d.updated_at}</p>
              </div>
            )}
          </div>
        </div>

        {err ? (
          <div className="card p-10 text-center text-[var(--text-secondary)]">Gagal memuat analisis. Coba lagi.</div>
        ) : needLogin ? (
          <div className="card p-12 text-center" data-testid="analysis-login-prompt">
            <Sparkles size={30} className="text-accent mx-auto mb-3" />
            <h3 className="font-heading font-bold text-lg mb-1">Analisis AI Eksklusif</h3>
            <p className="text-sm text-[var(--text-secondary)] mb-5 max-w-sm mx-auto">Masuk untuk membuka skor komprehensif 4 pilar (Bandarmology, Technical, Fundamental, News) untuk {ticker}.</p>
            <button onClick={login} data-testid="analysis-login-btn" className="inline-flex items-center gap-2 h-10 px-5 rounded-lg bg-[var(--accent)] text-[#07090E] font-semibold hover:bg-[var(--accent-hover)]"><LogIn size={16} /> Masuk dengan Google</button>
          </div>
        ) : !d ? (
          <div className="card p-16 text-center">
            <div className="w-10 h-10 border-2 border-[var(--accent)] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-[var(--text-secondary)] text-sm">AI sedang menganalisis {ticker}... (±15 detik)</p>
          </div>
        ) : (
          <>
            {/* Overall */}
            <div className="card p-5 mb-4">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <h3 className="font-heading text-2xl font-extrabold mb-2">Overall Score</h3>
                  <div className="flex gap-2 mb-4">
                    {["Technical", "Bandarmology", "Fundamental"].map((t) => (
                      <span key={t} className="text-[10px] px-2 py-0.5 rounded bg-[var(--surface-hover)] text-[var(--text-secondary)]">{t}</span>
                    ))}
                  </div>
                </div>
                <div className="w-20 h-20 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: scoreBg(d.overall_score) }} data-testid="overall-score">
                  <span className="font-heading text-4xl font-extrabold text-[#07090E]">{d.overall_score}</span>
                </div>
              </div>
              <div className="h-2 rounded-full bg-[var(--surface-base)] overflow-hidden"><div className="h-full rounded-full" style={{ width: `${d.overall_score}%`, backgroundColor: scoreColor(d.overall_score) }} /></div>
              <p className="text-xs text-[var(--text-secondary)] text-right mt-2">{d.overall_label}</p>
            </div>

            {/* 4 pillars */}
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
              {/* Bandarmology */}
              <div className="card p-4" data-testid="card-bandarmology">
                <CardHead title="Bandarmology" status={d.bandarmology?.status} score={d.bandarmology?.score} />
                <QuickInsight pattern={d.bandarmology?.pattern} />
                <div className="bg-[var(--surface-base)] rounded-lg p-3 border border-[var(--border-subtle)] mb-2">
                  <p className="text-xs font-semibold flex items-center gap-1.5 mb-2"><Globe size={13} className="text-accent" /> Foreign Flow</p>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div><span className="text-[var(--text-muted)] text-[10px]">Net</span><p className={`font-bold ${(d.bandarmology?.foreign_flow?.net || "").includes("BUY") ? "text-bull" : "text-bear"}`}>{d.bandarmology?.foreign_flow?.net} {d.bandarmology?.foreign_flow?.value}</p></div>
                    <div><span className="text-[var(--text-muted)] text-[10px]">Avg Asing</span><p className="font-mono-data font-bold">{d.bandarmology?.foreign_flow?.avg_price}</p><p className="text-[10px] text-[var(--text-muted)]">Floating P/L: {d.bandarmology?.foreign_flow?.floating_pl}</p></div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2 mb-2">
                  <Metric label="Lokal/Retail" value={d.bandarmology?.local_retail?.net} note={`Floating: ${d.bandarmology?.local_retail?.floating_pl}`} valueClass={(d.bandarmology?.local_retail?.net || "").includes("BUY") ? "text-bull" : "text-bear"} />
                  <Metric label="Avg Market" value={d.bandarmology?.local_retail?.avg_price} />
                </div>
                <Metric label="Akumulasi & Distribusi" value={d.bandarmology?.accumulation} />
              </div>

              {/* Technical */}
              <div className="card p-4" data-testid="card-technical">
                <CardHead title="Technical" status={d.technical?.status} score={d.technical?.score} />
                <QuickInsight pattern={d.technical?.pattern} />
                <div className="space-y-2">
                  <Metric label="Arah Tren Utama" tag="EMA" value={d.technical?.trend?.label} note={d.technical?.trend?.note} valueClass="text-bull" />
                  <Metric label="Kekuatan Tren" tag="MACD" value={d.technical?.macd?.label} note={d.technical?.macd?.note} valueClass={(d.technical?.macd?.label || "").includes("POS") ? "text-bull" : "text-bear"} />
                  <Metric label="Momentum Harga" tag="RSI" value={d.technical?.momentum?.label} note={d.technical?.momentum?.note} />
                  <Metric label="Validasi Volume" tag="Volume" value={d.technical?.volume?.label} note={d.technical?.volume?.note} valueClass="text-bull" />
                  <Metric label="Support & Resistance" tag="POC" value={d.technical?.support_resistance?.level} note={d.technical?.support_resistance?.note} valueClass="text-accent" />
                </div>
                <p className="text-[10px] text-[var(--text-muted)] text-center mt-3">Confidence {d.technical?.confidence} · Sektor {d.sector}</p>
              </div>

              {/* Fundamental */}
              <div className="card p-4" data-testid="card-fundamental">
                <CardHead title="Fundamental" status={d.fundamental?.status} score={d.fundamental?.score} />
                <QuickInsight pattern={d.fundamental?.pattern} />
                <div className="grid grid-cols-2 gap-x-3 gap-y-3 mb-3">
                  <MiniBar label="Valuation" value={d.fundamental?.valuation} />
                  <MiniBar label="Growth" value={d.fundamental?.growth} />
                  <MiniBar label="Profitability" value={d.fundamental?.profitability} />
                  <MiniBar label="Health" value={d.fundamental?.health} />
                </div>
                <div className="bg-[var(--surface-base)] rounded-lg p-3 border border-[var(--border-subtle)]">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider">Fair Value</span>
                    <span className={`text-[10px] font-bold ${statusPill(d.fundamental?.fair_value?.verdict).split(" ")[1]}`}>{d.fundamental?.fair_value?.verdict}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div><span className="text-[10px] text-[var(--text-muted)]">Current Price</span><p className="font-mono-data font-bold">Rp {d.price?.toLocaleString("id-ID")}</p></div>
                    <div><span className="text-[10px] text-[var(--text-muted)]">Fair Value</span><p className="font-mono-data font-bold">{d.fundamental?.fair_value?.fair}</p></div>
                  </div>
                  <p className="text-center font-heading font-extrabold text-lg mt-2" style={{ color: scoreColor(100 - Math.abs(d.fundamental?.fair_value?.discount_pct || 0)) }}>
                    {d.fundamental?.fair_value?.discount_pct > 0 ? "+" : ""}{d.fundamental?.fair_value?.discount_pct}%
                  </p>
                  <p className="text-[10px] text-[var(--text-muted)] text-center">{(d.fundamental?.fair_value?.discount_pct || 0) < 0 ? "Peluang beli signifikan" : "Selisih terhadap fair value"}</p>
                </div>
                <p className="text-[10px] text-[var(--text-muted)] mt-2">{d.fundamental?.sector_note}</p>
              </div>

              {/* News Sentiment */}
              <div className="card p-4" data-testid="card-news">
                <CardHead title="News Sentiment" status={d.news_sentiment?.status} score={d.news_sentiment?.score} />
                <p className="text-xs text-[var(--text-secondary)] mt-3 mb-3">{d.news_sentiment?.summary}</p>
                <p className="text-[11px] font-semibold text-[var(--text-muted)] uppercase tracking-wider mb-2">Latest News</p>
                <div className="space-y-2 max-h-[280px] overflow-y-auto pr-1">
                  {(d.news_sentiment?.latest || []).length === 0 ? (
                    <p className="text-xs text-[var(--text-muted)]">Belum ada berita spesifik untuk {ticker} saat ini.</p>
                  ) : (
                    d.news_sentiment.latest.map((n, i) => (
                      <a key={i} href={n.link} target="_blank" rel="noopener noreferrer" className="block bg-[var(--surface-base)] rounded-lg p-2.5 border border-[var(--border-subtle)] hover:border-[var(--accent)]/30 group">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-[9px] text-accent font-semibold">{n.source}</span>
                          <span className={`text-[9px] font-bold ${sentClass(n.sentiment)}`}>{n.sentiment}</span>
                          <ExternalLink size={10} className="ml-auto text-[var(--text-muted)] group-hover:text-accent" />
                        </div>
                        <p className="text-xs leading-snug group-hover:text-accent">{n.title}</p>
                      </a>
                    ))
                  )}
                </div>
              </div>
            </div>
            <p className="text-[10px] text-[var(--text-muted)] text-center mt-4">Skor & analisis dihasilkan AI berbasis data teknikal live + estimasi. Edukasi, bukan ajakan jual/beli. DYOR.</p>
          </>
        )}
      </motion.div>
    </div>
  );
}
